/**
 * WindowLevelSelection.js
 */

import {
    Game,
    GameWindow,
    OptionsMenuMode,
    SavePrefixType,
    UiButton,
    UiImage,
    UiText,
    UiScrollView,
    GameTween,
    GameAnimatedSprite,
    CoreEvents,
    GameTimer
} from "pixi_core";

import { Common } from "../game/Common";
import { HeroSelectionPopup } from "./HeroSelectionPopup";

import {
    CheatEvents,
    GameEvents,
    Tutorials,
    TutorialStep,
    ButtonGroups,
    MUSIC_FADE_IN_TIME,
    MUSIC_FADE_OUT_TIME,
    OPTIONS_PREFAB,
    NB_LEVELS,
    NB_COLORED_BUBBLES,
    TEMP_PATH_APPEARANCE_TIME,
    LAST_LEVEL,
    FIRST_LEVEL,
    LEVEL_SELECTION_SHAKE_OFFSET_X,
    LEVEL_SELECTION_SHAKE_OFFSET_Y,
    LEVEL_SELECTION_SHAKE_DURATION,
    GEAR_MENU_Y_POS,
    GEAR_MENU_X_POS,
    LEVEL_SELECTION_ARROWS_OFFSET,
    HERO_SELECTION_NAVIGATION_AXIS_RATIO,
    SETTINGS_PREFAB,
    Cinematic
} from "../Constants";

export class WindowLevelSelection extends GameWindow {
    constructor() {
        super({
            name: "WindowLevelSelection",
            isPreparationWindow: true
        });

        /** @private @type {Object} */
        this._savedData = null;

        /** @private @type {UiButton} */
        this._btnUnlockAll = null;

        /** @private @type {UiButton[]} */
        this._levelButtons = [];
        /** @private @type {{x: Number, y: Number}[]} */
        this._levelButtonsPositions = [];

        /** @private @type {UiImage[]} */
        this._levelButtonUpArrows = [];
        /** @private @type {UiImage[]} */
        this._levelButtonDownArrows = [];

        /** @private @type {UiButton[]} - Unlocked level button for keyboard/handset navigation */
        this._unlockedLevelButtons = [];

        /** @private @type {UiText[]} */
        this._levelButtonLabelsIdle = [];

        /** @private @type {UiText[]} */
        this._levelButtonLabelsOver = [];

        /** @private @type {UiText[]} */
        this._levelButtonLabelsPressed = [];

        /** @private @type {UiImage[]} */
        this._levelButtonLocks = [];

        /** @private @type {UiImage[]} */
        this._levelButtonCompleted = [];

        /** @private @type {UiButton[]} - TODO : Remove when no longer useful */
        this._debugButtons = [];

        /** @private @type {Number} */
        this._selectedLevel = 1;
        /** @private @type {Number} */
        this._highlightedLevel = 1;

        /** @private @type {Number} */
        this._lastUnlockedLevel = 0;

        /** @private @type {Number} */
        this._currentlyAppearingDot = 0;

        /** @private @type {Boolean} */
        this._advancingPath = false;

        /** @private @type {UiScrollView} */
        this._viewport = null;

        /** @private @type {Number} */
        this._defaultUiWidth = 0;

        /** @private @type {Number} */
        this._defaultViewportWidth = 0;

        /** @private @type {Number} */
        this._defaultViewportHeight = 0;

        /** @private @type {GameAnimatedSprite[][]} */
        this._levelPaths = [];

        /** @private @type {GameTimer[]} */
        this._timerDotAppearance = [];

        /** @private @type {HeroSelectionPopup} */
        this._heroSelection = new HeroSelectionPopup(this);

        Game.on(GameEvents.eLEVEL_SELECTION_SET_LEVEL, this._onSetSelectedLevel, this);
        Game.on(GameEvents.eLEVEL_MANAGER_GAME_PROGRESS, this._onGameProgress, this);
        Game.on(GameEvents.eTITLE_RESET, this._onResetProgression, this);

        Game.on(GameEvents.eHERO_SELECT_OPEN, this._onHeroSelectionOpen, this);
        Game.on(GameEvents.eLEVEL_SELECTION_UNLOCK_ALL_LEVELS, this._onBtnUnlockAll, this);

        Game.on(CoreEvents.eSCREEN_RESIZED, this._onScreenResized, this);
        Game.on(CoreEvents.eBUTTON_SELECTED, this._onButtonSelected, this);
        Game.on(CoreEvents.eBUTTON_DESELECTED, this._onButtonDeselected, this);

        Game.on(CheatEvents.eLEVEL_SELECTION_SET_LEVEL_DEBUG, this._onSetSelectedLevel, this);
    }

    /**
     * The window is opening for the first time
     * @protected
     */
    _onFirstWindowOpening() {
        const ui = Game.ui.instantiate("LevelSelection", this);

        this._viewport = ui.findElementByName("Viewport");
        this._defaultUiWidth = ui.width;
        this._defaultViewportWidth = this._viewport.width;
        this._defaultViewportHeight = this._viewport.height;

        this._btnUnlockAll = ui.findElementByName("BtnUnlockAll");
        const pathContent = Game.ui.instantiate("LevelSelectionPath", this);

        if (pathContent) {
            pathContent.calculateLimits();
            this._viewport.setContent({
                page: 1,
                content: pathContent
            });
        }

        for (let i = 0; i < NB_LEVELS; ++i) {
            let levelButtonName = "BtnLevel" + (i + 1).toString();
            let levelButton = pathContent.findElementByName(levelButtonName);
            let levelButtonLabelIdle = levelButton.findElementByName("Idle").findElementByName("LblLevel");
            let levelButtonLabelOver = levelButton.findElementByName("Over").findElementByName("LblLevel");
            let levelButtonLabelPressed = levelButton.findElementByName("Pressed").findElementByName("LblLevel");
            let levelButtonLock = levelButton.findElementByName("ImgLocked");
            let levelButtonCompleted = levelButton.findElementByName("ImgComplete");

            levelButton.groupId = "Levels";

            let arrowUp = levelButton.createImage({
                idSprite: "IMG_AttackParticleTop02",
                x: 0,
                y: -LEVEL_SELECTION_ARROWS_OFFSET
            });
            let arrowDown = levelButton.createImage({
                idSprite: "IMG_AttackParticleTop02",
                x: 0,
                y: LEVEL_SELECTION_ARROWS_OFFSET
            });
            arrowDown.sprite.angle = 180;

            this._levelButtonUpArrows.push(arrowUp);
            this._levelButtonDownArrows.push(arrowDown);
            arrowUp.visible = false;
            arrowDown.visible = false;

            this._levelButtons.push(levelButton);
            this._levelButtonsPositions.push({ x: levelButton.x, y: levelButton.y });

            this._levelButtonLabelsIdle.push(levelButtonLabelIdle);
            this._levelButtonLabelsOver.push(levelButtonLabelOver);
            this._levelButtonLabelsPressed.push(levelButtonLabelPressed);
            this._levelButtonLocks.push(levelButtonLock);
            this._levelButtonCompleted.push(levelButtonCompleted);

            let levelGroupName = "Level" + (i + 1).toString();
            let levelGroup = pathContent.findElementByName(levelGroupName);
            let levelGroupCut = [];

            for (let j = 0; j < NB_COLORED_BUBBLES; ++j) {
                let dotName = "ImgDot" + j.toString();
                let dot = levelGroup.findElementByName(dotName);

                if (dot) {
                    let animatedDot = new GameAnimatedSprite(null, Game.design.getGraphicData("LevelSelectDot"));
                    levelGroupCut.push(animatedDot);
                    pathContent.uiRoot.addChild(animatedDot);
                    animatedDot.x = dot.x;
                    animatedDot.y = dot.y;
                    animatedDot.playAnimation("locked");
                    dot.setVisible(false);
                }
            }

            this._levelPaths.push(levelGroupCut);
        }

        this._debugButtons = [
            ui.findElementByName("BtnLevelDebug1"),
            ui.findElementByName("BtnLevelDebug2"),
            ui.findElementByName("BtnLevelDebug3"),
            ui.findElementByName("BtnLevelDebug4"),
            ui.findElementByName("BtnLevelDebug5"),
            ui.findElementByName("BtnLevelDebug6")
        ];

        if (!Game.build.isDebug) {
            for (const debugButton of this._debugButtons) {
                debugButton.visible = false;
            }
        }

        this._selectedLevel = Common.save.getProgression();
        this._highlightedLevel = this._selectedLevel;

        this._heroSelection.initialize(ui);
    }

    /**
     * The window is opening
     * @protected
     */
    _onWindowOpening() {
        // Configure the options menu for this context
        Game.windows.optionsMenu.reset({
            prefab: OPTIONS_PREFAB,
            prefabSettings: SETTINGS_PREFAB,
            mode: OptionsMenuMode.eOPTIONS,
            exclude: ["BtnRestart", "BtnLevelSelection"]
        });

        // Configure gear button for this context
        Game.windows.showGearButton({
            fullScreen: true,
            idSprite: "BTN_Gear",
            idSpriteOver: "BTN_GearHover",
            idSpritePressed: "BTN_GearPressed",
            idSpriteBack: "BTN_Back",
            idSpriteBackOver: "BTN_BackHover",
            idSpriteBackPressed: "BTN_BackPressed",
            scalePressed: 1,
            x: GEAR_MENU_X_POS,
            y: GEAR_MENU_Y_POS
        });

        // Music
        Game.audio.playMusic("IDSND_MUSIC_MENU", { fadeOutTime: MUSIC_FADE_OUT_TIME, fadeInTime: MUSIC_FADE_IN_TIME });

        Game.save
            .getObject("SaveGameData", SavePrefixType.eGAME)
            .then((data) => {
                this._savedData = data;
            })
            .catch((error) => {
                this._savedData = { score: 0 };
            });

        this._heroSelection.loadHeroTeam();

        this._resetLevelButtons();
        this._onScreenResized();

        this._onSetUnlockedLevels(Common.save.getProgression());
        this._btnUnlockAll.setVisible(this._lastUnlockedLevel < LAST_LEVEL);

        this._resetNavigationSettings();
    }

    /**
     * @protected
     * @param {UiButton} [defaultButton]
     */
    _resetNavigationSettings(defaultButton = null) {
        this.resetNavigationButtonGroups();

        if (this._lastUnlockedLevel < NB_LEVELS) {
            this.setHorizontalMultiGroupNavigation([ButtonGroups.eCHANGE_TEAM, ButtonGroups.eLEVELS, ButtonGroups.eUNLOCK_ALL], true);
        } else {
            this.setHorizontalMultiGroupNavigation([ButtonGroups.eCHANGE_TEAM, ButtonGroups.eLEVELS], true);
        }

        const lastUnlockedLevelButton = this._unlockedLevelButtons[0];
        this.setNavigationButtons(ButtonGroups.eLEVELS, this._unlockedLevelButtons, lastUnlockedLevelButton);

        if (defaultButton) {
            this.setDefaultNavigationGroup(defaultButton.groupId);
        } else {
            this.setDefaultNavigationGroup(lastUnlockedLevelButton.groupId);
            this.setDefaultNavigationButton(lastUnlockedLevelButton);
        }
    }

    /**
     * The window is opened
     * @protected
     */
    _onWindowOpened() {
        Game.fire(GameEvents.eGAME_SET_LEVEL, this._selectedLevel);

        // Tutorial trigger - Hero Selection
        // Force player to open hero selection menu, tutorial will be completed when closing it
        if (
            Common.tutorial.openTutorial(
                Tutorials.eHERO_SELECTION,
                TutorialStep.eCHANGE_TEAM,
                this._onHeroSelectionTutorialCompleted.bind(this)
            )
        ) {
            this._enableLevelButtons(false);
            this._viewport.disable();

            this.setHorizontalMultiGroupNavigation([ButtonGroups.eCHANGE_TEAM], false);
            this.setCurrentNavigationButton(this._heroSelection.btnHeroSelection);
        }
    }

    /**
     * @private
     */
    _onHeroSelectionTutorialCompleted() {
        this._enableLevelButtons(true);
        this._viewport.enable();

        this._resetNavigationSettings();
    }

    /**
     * @private
     * @param {Boolean} enable
     */
    _enableLevelButtons(enable) {
        for (const levelButton of this._levelButtons) {
            if (enable) {
                levelButton.enable();
            } else {
                levelButton.disable();
            }
        }

        for (const debugButton of this._debugButtons) {
            if (enable) {
                debugButton.enable();
            } else {
                debugButton.disable();
            }
        }

        if (enable) {
            this._btnUnlockAll.enable();
        } else {
            this._btnUnlockAll.disable();
        }
    }

    /**
     * The window is closed
     * @protected
     */
    _onWindowClosed() {
        this._heroSelection.forceClose();
    }

    /**
     * The hero selection popup button has been tapped
     * @private
     */
    _onHeroSelectionOpen() {
        this._heroSelection.open(this._onHeroSelectionOpened.bind(this), this._onHeroSelectionClosed.bind(this));

        this._enableLevelButtons(false);
        this._viewport.disable();

        this.resetNavigationButtonGroups();
        this.setDefaultNavigationGroup(ButtonGroups.eAVENGERS);
        this.setNavigationNeighborAxisRatio(ButtonGroups.eAVENGERS, HERO_SELECTION_NAVIGATION_AXIS_RATIO);
        this.setDefaultNavigationButton(this._heroSelection.btnConfirm);
    }

    /**
     * The hero selection popup button has been tapped
     * @private
     */
    _onHeroSelectionOpened() {
        if (this.shouldActivateNavigation() && !Game.isPaused("Tutorial")) {
            this.setCurrentNavigationButton(this._heroSelection.btnConfirm);
            this.navigationStart();
        }
    }

    /**
     * @private
     */
    _onHeroSelectionClosed() {
        this._enableLevelButtons(true);
        this._viewport.enable();

        this._resetNavigationSettings(this._heroSelection.btnHeroSelection);

        if (this.shouldActivateNavigation()) {
            this.setCurrentNavigationButton(this._heroSelection.btnHeroSelection);
            this.navigationStart();
        }
    }

    /**
     * @private
     */
    _onBtnUnlockAll() {
        Game.windows.messageBox.open({
            message: Game.localization.getText("IDS_MSGBOX_UNLOCK_ALL_LEVELS"),
            onConfirm: this._onUnlockAllConfirmed.bind(this)
        });
    }

    /**
     * @private
     */
    _onUnlockAllConfirmed() {
        this._onUnlockAll();
        Game.fire(GameEvents.eLEVEL_SELECTION_UNLOCK_ALL_LEVELS_CONFIRMED);
    }

    /**
     * Setting the levels to be unlocked.
     * @private
     * @param {Number} level
     */
    _onSetUnlockedLevels(level) {
        this._lastUnlockedLevel = level;

        for (let i = 0; i < NB_LEVELS; ++i) {
            for (let j = 0; j < this._levelPaths[i].length; ++j) {
                if (i < this._lastUnlockedLevel - (this._advancingPath ? 1 : 0)) {
                    this._levelPaths[i][j].playAnimation("unlocked");
                } else if (i >= this._lastUnlockedLevel - (this._advancingPath ? 1 : 0)) {
                    this._levelPaths[i][j].playAnimation("locked");
                }
            }
        }

        this._resetLevelButtons();

        if (this._advancingPath) {
            this._scrollToLevel(level);
            this._unlockLevelPath();
            this._advancingPath = false;
        } else {
            this._onSetSelectedLevel(this._selectedLevel, false);
        }
    }

    /**
     * The window is destroyed
     * @private
     */
    _unlockLevelPath() {
        this._currentlyAppearingDot = 0;

        for (let i = 0; i < this._levelPaths[this._lastUnlockedLevel - 1].length; ++i) {
            let timerDotAppearance = Game.windows
                .getCurrentWindow()
                .createTimer(TEMP_PATH_APPEARANCE_TIME / this._levelPaths[this._lastUnlockedLevel - 1].length, { autoStart: false });
            timerDotAppearance.onStart = () => {
                Game.audio.playSfx("IDSND_LVL_PATH_LIGHT_UP");
                this._levelPaths[this._lastUnlockedLevel - 1][i].playAnimation("unlocking");
            };
            timerDotAppearance.onComplete = () => {
                ++this._currentlyAppearingDot;
                if (this._currentlyAppearingDot < this._levelPaths[this._lastUnlockedLevel - 1].length) {
                    this._timerDotAppearance[this._currentlyAppearingDot].start();
                } else {
                    this._onSetSelectedLevel(this._lastUnlockedLevel, false);
                    this._destroyDotTimers();
                }
            };
            this._timerDotAppearance.push(timerDotAppearance);
        }

        if (this._timerDotAppearance) {
            this._timerDotAppearance[0].start();
        }
    }

    /**
     * Destroy the dot timers
     * @private
     */
    _destroyDotTimers() {
        for (let i = 0; i < this._timerDotAppearance.length; ++i) {
            this._timerDotAppearance[i].destroy();
            this._timerDotAppearance[i] = null;
        }

        this._timerDotAppearance = [];
    }

    /**
     * Set the selected level
     * @private
     * @param {Number} level
     * @param {Boolean} goToGame
     */
    _onSetSelectedLevel(level, goToGame = true) {
        let levelNumber = Game.utils.assertNumber(level, 0);

        // Locked level ==> visual + audio feedback
        if (levelNumber > this._lastUnlockedLevel && levelNumber <= NB_LEVELS) {
            Game.audio.playSfx("IDSND_LVL_PLAYER_DENIED");
            this._shakeButton(level);
            return;
        }

        this._selectedLevel = levelNumber;
        this._highlightedLevel = levelNumber;

        this._resetLevelButtons();
        Game.fire(GameEvents.eGAME_SET_LEVEL, this._selectedLevel);

        if (goToGame) {
            Game.audio.playSfx("IDSND_NAVIG_FORWARD");

            if (this._selectedLevel === FIRST_LEVEL) {
                Game.fire(GameEvents.eCINEMATIC_SET, Cinematic.eINTRO);
                Game.windows.changeContext("WindowCinematics");
            } else {
                Game.windows.changeContext("WindowGame");
            }
        } else {
            // Reset the last unlocked button to the selected state, as the states of the buttons just got modified
            if (this.shouldActivateNavigation()) {
                const lastUnlockedLevelButton = this._unlockedLevelButtons[0];
                lastUnlockedLevelButton.setSelected(true);
            }
        }
    }

    /**
     * @private
     * @param {Number} level
     */
    _shakeButton(level) {
        const levelButton = this._levelButtons[level - 1];
        const levelButtonPosition = this._levelButtonsPositions[level - 1];

        const initialX = levelButtonPosition.x;
        const initialY = levelButtonPosition.y;

        const tweenShake = this.createTween(null, true)
            .to(null, LEVEL_SELECTION_SHAKE_DURATION)
            .easing(GameTween.Easing.Linear)
            .onUpdate(() => {
                levelButton.x = initialX - LEVEL_SELECTION_SHAKE_OFFSET_X + Math.random() * LEVEL_SELECTION_SHAKE_OFFSET_X * 2;
                levelButton.y = initialY - LEVEL_SELECTION_SHAKE_OFFSET_Y + Math.random() * LEVEL_SELECTION_SHAKE_OFFSET_Y * 2;
            })
            .onComplete(() => {
                levelButton.x = initialX;
                levelButton.y = initialY;
                tweenShake.destroy();
            })
            .start();
    }

    /**
     * @private
     */
    _resetLevelButtons() {
        this._unlockedLevelButtons = [];

        for (let levelIndex = 0; levelIndex < NB_LEVELS; ++levelIndex) {
            const levelButton = this._levelButtons[levelIndex];
            const levelButtonLabelIdle = this._levelButtonLabelsIdle[levelIndex];
            const levelButtonLabelOver = this._levelButtonLabelsOver[levelIndex];
            const levelButtonLabelPressed = this._levelButtonLabelsPressed[levelIndex];
            const levelButtonLock = this._levelButtonLocks[levelIndex];
            const levelButtonCompleted = this._levelButtonCompleted[levelIndex];
            levelButtonCompleted.setVisible(levelIndex < this._lastUnlockedLevel - 1 || this._lastUnlockedLevel === NB_LEVELS);

            if (levelIndex < this._lastUnlockedLevel) {
                this._unlockedLevelButtons.push(levelButton);
            }

            if (levelIndex === this._selectedLevel - 1) {
                // Disabling the current level button because it's selected
                levelButton.setSprite("BTN_Level");
                levelButtonLock.setVisible(false);
                levelButtonLabelIdle.setVisible(true);
                levelButtonLabelOver.setVisible(true);
                levelButtonLabelPressed.setVisible(true);
                levelButtonLabelIdle.setTextStyle("LevelSelection_Idle");
            } else {
                if (levelIndex < this._lastUnlockedLevel) {
                    levelButton.setSprite("BTN_Level");
                    levelButtonLock.setVisible(false);
                    levelButtonLabelIdle.setVisible(true);
                    levelButtonLabelOver.setVisible(true);
                    levelButtonLabelPressed.setVisible(true);
                    levelButtonLabelIdle.setTextStyle("LevelSelectionCompleted_Idle");
                } else {
                    levelButton.setSprite("BTN_Level");
                    levelButtonLock.setVisible(true);
                    levelButtonLabelIdle.setVisible(false);
                    levelButtonLabelOver.setVisible(false);
                    levelButtonLabelPressed.setVisible(false);
                }
            }
        }
        this._unlockedLevelButtons.reverse();
    }

    /**
     * Telling the level selection window that it should do the next level unlocking animation.
     * @private
     */
    _onGameProgress() {
        this._advancingPath = true;
    }

    /**
     * When resetting the progression in the title window.
     * @private
     */
    _onResetProgression() {
        this._selectedLevel = FIRST_LEVEL;
        this._highlightedLevel = this._selectedLevel;
    }

    /**
     * @private
     */
    _onUnlockAll() {
        for (let i = 0; i < NB_LEVELS; ++i) {
            for (let j = 0; j < this._levelPaths[i].length; ++j) {
                this._levelPaths[i][j].playAnimation("unlocked");
            }
        }

        this._lastUnlockedLevel = LAST_LEVEL;
        this._onSetSelectedLevel(this._lastUnlockedLevel, false);

        this._scrollToLevel(this._selectedLevel);

        this._btnUnlockAll.setVisible(false);

        this._resetLevelButtons();

        this._resetNavigationSettings();
    }

    /**
     * @private
     * @param {Number} levelNumber
     */
    _scrollToLevel(levelNumber) {
        // Consider debug levels >NB_LEVELS
        if (levelNumber > NB_LEVELS) {
            levelNumber = NB_LEVELS;
        }

        // TODO: Scroll with animation
        this._viewport.setScrollY(-this._levelButtons[levelNumber - 1].y - this._viewport.getCurrentPageHeight() / 2, false);
    }

    /**
     * A button has been selected through keyboard/handset navigation
     * We might need to scroll the viewport
     * @private
     * @param {UiButton} button
     */
    _onButtonSelected(button) {
        if (button.groupId === ButtonGroups.eLEVELS) {
            this._highlightedLevel = Number(button.extraParam);
            this._scrollToLevel(this._highlightedLevel);

            const upArrow = this._levelButtonUpArrows[this._highlightedLevel - 1];
            const downArrow = this._levelButtonDownArrows[this._highlightedLevel - 1];
            upArrow.visible = this._highlightedLevel < this._lastUnlockedLevel;
            downArrow.visible = this._highlightedLevel > 1;
        }
    }

    /**
     * A button has been selected through keyboard/handset navigation
     * We might need to scroll the viewport
     * @private
     * @param {UiButton} button
     */
    _onButtonDeselected(button) {
        if (button.groupId === ButtonGroups.eLEVELS) {
            const level = Number(button.extraParam);

            const upArrow = this._levelButtonUpArrows[level - 1];
            const downArrow = this._levelButtonDownArrows[level - 1];
            upArrow.visible = false;
            downArrow.visible = false;
        }
    }

    /**
     * @private
     */
    _onScreenResized() {
        if (!this._viewport) return;

        let width = this._defaultViewportWidth;
        if (Game.screen.isPortrait) {
            width = this._defaultUiWidth;
        } else {
            width = this._defaultViewportWidth;
        }

        this._viewport.setSize(width, Game.viewport.height);
        this._scrollToLevel(this._highlightedLevel);
    }
}
